import {
	Button,
	ButtonGroup,
	ClickAwayListener,
	Divider,
	Grow,
	IconButton,
	makeStyles,
	MenuItem,
	MenuList,
	Paper,
	Popper,
	TooltipProps,
} from "@material-ui/core";
import { ArrowDropDown, ClearRounded, Search } from "@material-ui/icons";
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import MappedComponent from "./mapped-components/MappedComponent";
import { LyfeTooltip, TextField } from "@lyfegen-platform/react-common";
import { useTranslation } from "react-i18next";

export interface IDropDownButtonMenuOption {
	id: string;
	label: string;
	link?: string;
	tooltipMessage?: string;
	onClick?: (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
	attributes?: any;
}

interface IProps {
	defaultText: string;
	menuOptions: Array<IDropDownButtonMenuOption>;
	variant?: "text" | "outlined" | "contained";
	optionRender?: (option: IDropDownButtonMenuOption) => React.ReactElement;
	disabledOptionsIndex?: Array<number>;
	selectedOptionIndex?: number;
	startIcon?: React.ReactElement | boolean;
	preOptionText?: string;
	postOptionText?: string;
	buttonTooltip?: string;
	color?: "inherit" | "primary" | "secondary";
	isDisabled?: boolean;
	onClick?: (option: IDropDownButtonMenuOption) => void;
	searchThreshold?: number;
	onOpen?: () => void;
}

const DropDownButton = (props: IProps) => {
	const classes = useStyles({});
	const { push } = useHistory();
	const buttonRef = useRef(null);
	const buttonWidthRef = useRef<HTMLButtonElement>(null);
	const menuWidthRef = useRef<HTMLUListElement>(null);
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [searchText, setSearchText] = useState("");
	const {
		defaultText,
		menuOptions,
		optionRender,
		disabledOptionsIndex,
		selectedOptionIndex,
		preOptionText,
		postOptionText,
		color,
		buttonTooltip,
		onClick,
		isDisabled,
		variant = "contained",
		searchThreshold,
		onOpen,
	} = props;
	const [, setButtonWidth] = useState(Number);
	const filteredOptions = menuOptions.filter((option) => option.label.toLowerCase().includes(searchText.toLowerCase()));

	const maxContentLength: number =
		menuOptions.reduce((prev, current) => (current.label.length > prev.label.length ? current : prev), { label: "" }).label.length +
		150;

	useEffect(() => {
		if (buttonWidthRef.current) {
			setButtonWidth(
				maxContentLength > buttonWidthRef.current.clientWidth + 20 ? maxContentLength : buttonWidthRef.current.clientWidth
			);
		}
	}, [buttonWidthRef, maxContentLength]);

	function handleToggle() {
		const newOpen = !open;
		setOpen(newOpen);
		if (newOpen) onOpen?.();
	}

	function handleClose() {
		setOpen(false);
	}

	function handleOptionSelected(event: React.MouseEvent<HTMLLIElement, MouseEvent>, option: IDropDownButtonMenuOption) {
		handleClose();
		option.onClick ? option.onClick(event) : push(option.link || "/");
		onClick && onClick(option);
	}

	function defaultRender(option: IDropDownButtonMenuOption) {
		return (
			<>
				{preOptionText} {option.label} {postOptionText}
			</>
		);
	}

	return (
		<div ref={buttonRef}>
			<Popper
				open={open}
				anchorEl={buttonRef.current}
				role={undefined}
				transition
				className={classes.options}
				placement="bottom-start"
				style={{ zIndex: 4 }}
				popperOptions={{
					modifiers: {
						preventOverflow: {
							enabled: true,
							boundariesElement: "window",
						},
						flip: {
							enabled: true,
						},
					},
				}}
			>
				{({ TransitionProps, placement }) => (
					<Grow {...TransitionProps} style={{ transformOrigin: placement.startsWith("bottom") ? "center top" : "center bottom" }}>
						<Paper className={classes.container}>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList id="split-button-menu" ref={menuWidthRef}>
									{searchThreshold && menuOptions.length > searchThreshold && (
										<>
											<TextField
												variant="outlined"
												placeholder={` ${t("search")}`}
												fullWidth
												size="small"
												value={searchText}
												onChange={(e) => setSearchText(e.target.value)}
												className={classes.searchField}
												InputProps={{
													startAdornment: <Search className={classes.searchIcon} color="primary" />,
													endAdornment: searchText ? (
														<IconButton onClick={() => setSearchText("")}>
															<ClearRounded />
														</IconButton>
													) : (
														""
													),
												}}
											/>
											<Divider style={{ flexGrow: 1, backgroundColor: "#cddcdc" }} />
										</>
									)}
									{filteredOptions.map((option: IDropDownButtonMenuOption, index: number) => {
										const props = {
											placement: "left-start" as TooltipProps["placement"],
											title: option.tooltipMessage || "",
											children: (
												<span>
													<MenuItem
														button
														id={option.id}
														disabled={
															disabledOptionsIndex &&
															disabledOptionsIndex.includes(menuOptions.indexOf(option))
														}
														selected={index === selectedOptionIndex}
														onClick={(e: React.MouseEvent<HTMLLIElement, MouseEvent>) =>
															handleOptionSelected(e, option)
														}
														{...option.attributes}
													>
														{(optionRender || defaultRender)(option)}
													</MenuItem>
												</span>
											),
										};

										return <MappedComponent key={index} component="Tooltip" props={props} />;
									})}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
			<LyfeTooltip title={buttonTooltip || ""}>
				<ButtonGroup
					variant={variant}
					color={color ? color : "inherit"}
					ref={buttonRef}
					aria-label="split button"
					style={{ boxShadow: "none" }}
				>
					<Button
						ref={buttonWidthRef}
						style={{ minWidth: "106px", paddingLeft: "16px", paddingRight: "16px", whiteSpace: "nowrap" }}
						onClick={handleToggle}
						classes={{
							label: classes.buttonLabel,
						}}
						disabled={isDisabled}
						startIcon={props.startIcon}
					>
						{defaultText}
						<ArrowDropDown />
					</Button>
				</ButtonGroup>
			</LyfeTooltip>
		</div>
	);
};

export default DropDownButton;

const useStyles = makeStyles(({ palette }) => ({
	options: {
		zIndex: 1201,
		position: "relative",
	},
	container: {
		borderRadius: "0 0 4px 4px",
		maxHeight: "550px",
		overflowY: "auto",
	},
	buttonLabel: {
		justifyContent: "space-between",
	},
	searchField: {
		padding: "8px",
		"&:hover .MuiOutlinedInput-notchedOutline": {
			borderColor: "white",
		},
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: "white",
		},
		"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderColor: "white",
		},
	},
	searchIcon: {
		marginLeft: "-10px",
	},
}));
